import $ from "jquery";

import * as blueslip from "./blueslip.ts";

// Track visibility and current paths for both sidebars
let left_visible = false;
let right_visible = false;
let current_left_path: string | undefined;
let current_right_path: string | undefined;
let default_left_sidebar_html: string | undefined;
let default_right_sidebar_html: string | undefined;

export function is_left_visible(): boolean {
    return left_visible;
}

export function is_right_visible(): boolean {
    return right_visible;
}

export function get_current_left_path(): string | undefined {
    return current_left_path;
}

export function get_current_right_path(): string | undefined {
    return current_right_path;
}

// Create or get custom content container within sidebar
function get_or_create_left_custom_container(): JQuery {
    let $container = $("#left-sidebar-realm-custom");
    if ($container.length === 0) {
        // Store the original sidebar state before adding custom container
        if (default_left_sidebar_html === undefined) {
            const $leftSidebar = $("#left-sidebar");
            if ($leftSidebar.length > 0) {
                default_left_sidebar_html = $leftSidebar.html();
            }
        }
        
        // Create custom container and hide default sidebar content
        $("#left-sidebar").children().hide();
        $container = $('<div id="left-sidebar-realm-custom"></div>');
        $("#left-sidebar").append($container);
    }
    return $container;
}

function get_or_create_right_custom_container(): JQuery {
    let $container = $("#right-sidebar-realm-custom");
    if ($container.length === 0) {
        // Store the original sidebar state before adding custom container
        if (default_right_sidebar_html === undefined) {
            const $rightSidebar = $("#right-sidebar");
            if ($rightSidebar.length > 0) {
                default_right_sidebar_html = $rightSidebar.html();
            }
        }
        
        // Create custom container and hide default sidebar content
        $("#right-sidebar").children().hide();
        $container = $('<div id="right-sidebar-realm-custom"></div>');
        $("#right-sidebar").append($container);
    }
    return $container;
}

// Inject auto-reload script specifically for sidebar content
function inject_sidebar_reload_script(
    html: string,
    path: string,
    target_id: string,
): string {
    // Remove any existing auto-reload script from the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Remove all script tags that contain auto-reload markers
    const scripts = doc.querySelectorAll("script");
    scripts.forEach((script) => {
        const scriptContent = script.textContent || "";
        if (
            scriptContent.includes("realm-static-etag") ||
            scriptContent.includes("checkForUpdates")
        ) {
            script.remove();
        }
    });

    // Get body content
    const bodyContent = doc.body ? doc.body.innerHTML : html;

    // Create new auto-reload script specifically for sidebar
    const reloadScript = `
<script data-sidebar-auto-reload="true">
(function() {
    const targetContainer = document.getElementById('${target_id}');
    if (!targetContainer) return;

    const currentPath = '${path.replace(/'/g, "\\'")}';
    let lastEtag = null;

    function saveFormState() {
        const formData = new Map();
        targetContainer.querySelectorAll('input, textarea, select').forEach((element) => {
            const el = element;
            const name = el.name || el.id;
            if (!name) return;

            if (el.type === 'checkbox' || el.type === 'radio') {
                formData.set(name, {value: el.value, checked: el.checked});
            } else {
                formData.set(name, {value: el.value});
            }
        });
        return formData;
    }

    function restoreFormState(formData) {
        formData.forEach((data, name) => {
            const elements = targetContainer.querySelectorAll(\`[name="\${name}"], [id="\${name}"]\`);
            elements.forEach((element) => {
                const el = element;
                if (el.type === 'checkbox' || el.type === 'radio') {
                    el.checked = data.checked || false;
                } else {
                    el.value = data.value || '';
                }
            });
        });
    }

    function reloadSidebarContent() {
        fetch(currentPath, {
            method: 'HEAD',
            credentials: 'same-origin'
        })
        .then(response => {
            const etag = response.headers.get('ETag');
            if (etag && etag !== lastEtag) {
                lastEtag = etag;
                const formData = saveFormState();

                return fetch(currentPath, {
                    credentials: 'same-origin',
                    headers: {'X-Requested-With': 'XMLHttpRequest'}
                })
                .then(r => r.text())
                .then(newHtml => {
                    const parser = new DOMParser();
                    const newDoc = parser.parseFromString(newHtml, 'text/html');
                    
                    // Remove old auto-reload script
                    const scripts = newDoc.querySelectorAll('script');
                    scripts.forEach(script => {
                        const content = script.textContent || '';
                        if (content.includes('realm-static-etag') || content.includes('checkForUpdates')) {
                            script.remove();
                        }
                    });

                    const newBody = newDoc.body ? newDoc.body.innerHTML : newHtml;
                    const wrapped = \`<div style="padding-top: 2.5em;">\${newBody}</div>\`;
                    targetContainer.innerHTML = wrapped;
                    
                    restoreFormState(formData);
                    
                    // Re-inject this script
                    const scriptTag = document.createElement('script');
                    scriptTag.setAttribute('data-sidebar-auto-reload', 'true');
                    scriptTag.textContent = document.currentScript.textContent;
                    targetContainer.appendChild(scriptTag);
                });
            }
        })
        .catch(() => {});
    }

    // Poll every 3 seconds
    setInterval(reloadSidebarContent, 3000);
})();
</script>`;

    // Wrap content with inline padding and add reload script
    return `<div style="padding-top: 2.5em;">${bodyContent}</div>${reloadScript}`;
}

function fetch_and_display_left(path: string): void {
    // Ensure path starts with /
    if (!path.startsWith("/")) {
        path = "/" + path;
    }

    const $leftSidebar = $("#left-sidebar");
    if ($leftSidebar.length === 0) {
        blueslip.error("Left sidebar element not found");
        return;
    }

    const $container = get_or_create_left_custom_container();
    $container.html('<div class="sidebar-loading">Loading...</div>');

    // Fetch the content directly from the path (served by realm static middleware)
    fetch(path, {
        credentials: "same-origin",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
        },
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            return response.text();
        })
        .then((html) => {
            // Inject auto-reload script for sidebar
            const processedHtml = inject_sidebar_reload_script(html, path, "left-sidebar-realm-custom");
            $container.html(processedHtml);

            left_visible = true;
            current_left_path = path;
        })
        .catch((error) => {
            blueslip.error("Failed to load left sidebar content", {path, error});
            $container.html(
                '<div class="sidebar-error">Failed to load content</div>',
            );
        });
}

function fetch_and_display_right(path: string): void {
    // Ensure path starts with /
    if (!path.startsWith("/")) {
        path = "/" + path;
    }

    const $rightSidebar = $("#right-sidebar");
    if ($rightSidebar.length === 0) {
        blueslip.error("Right sidebar element not found");
        return;
    }

    const $container = get_or_create_right_custom_container();
    $container.html('<div class="sidebar-loading">Loading...</div>');

    // Fetch the content directly from the path (served by realm static middleware)
    fetch(path, {
        credentials: "same-origin",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
        },
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            return response.text();
        })
        .then((html) => {
            // Inject auto-reload script for sidebar
            const processedHtml = inject_sidebar_reload_script(html, path, "right-sidebar-realm-custom");
            $container.html(processedHtml);

            right_visible = true;
            current_right_path = path;
        })
        .catch((error) => {
            blueslip.error("Failed to load right sidebar content", {path, error});
            $container.html(
                '<div class="sidebar-error">Failed to load content</div>',
            );
        });
}

export function show_left(path: string): void {
    fetch_and_display_left(path);
}

export function show_right(path: string): void {
    fetch_and_display_right(path);
}

export function hide_left(): void {
    if (!left_visible) {
        return;
    }

    // Remove custom container and restore default sidebar
    const $container = $("#left-sidebar-realm-custom");
    if ($container.length > 0) {
        $container.remove();
    }
    
    // Show the default sidebar children that were hidden
    $("#left-sidebar").children().show();

    left_visible = false;
    current_left_path = undefined;
}

export function hide_right(): void {
    if (!right_visible) {
        return;
    }

    // Remove custom container and restore default sidebar
    const $container = $("#right-sidebar-realm-custom");
    if ($container.length > 0) {
        $container.remove();
    }
    
    // Show the default sidebar children that were hidden
    $("#right-sidebar").children().show();

    right_visible = false;
    current_right_path = undefined;
}

export function hide_both(): void {
    hide_left();
    hide_right();
}
